// ── Web Push Notifications ───────────────────────────────────────
// Uses the standard Web Push API with VAPID keys.
// Service worker receives push events even when the tab is closed.
// Notifications show on lock screen on Android/Desktop.

const STORAGE_KEY = 'bchnostr:push-enabled';

/** Check if Web Push is supported */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Register the app's service worker for ALL users (not just those who enabled
 * push). This is what makes BCHNostr an installable, offline-capable PWA:
 * the worker caches the app shell + static assets and answers navigations when
 * offline. It also doubles as the Web Push worker, so push subscription later
 * reuses this same registration.
 *
 * Registration is deferred until after `load` so it never competes with the
 * initial app render for bandwidth. Failures are swallowed — a missing SW
 * just means no offline support, never a broken app.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  const register = () => {
    navigator.serviceWorker.register('/sw-push.js').catch(() => {});
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}

/** Check if the user has already enabled push notifications (sync check) */
export function isPushEnabled(): boolean {
  // Quick sync check — but we also verify async on the notifications page
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'webpush';
  } catch {
    return false;
  }
}

/** Convert a base64url VAPID key to Uint8Array for PushManager */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Enable push notifications.
 * 1. Register service worker
 * 2. Get VAPID public key from server
 * 3. Subscribe to Web Push
 * 4. Send subscription to server
 */
export async function enablePushNotifications(pubkey: string): Promise<{ ok: boolean; error?: string }> {
  if (!isPushSupported()) {
    return { ok: false, error: 'Push notifications are not supported in this browser.' };
  }

  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, error: 'Notification permission was denied.' };
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;

    // Get VAPID public key from server
    const setupRes = await fetch('/api/push-setup');
    if (!setupRes.ok) {
      return { ok: false, error: 'Failed to get push configuration from server.' };
    }
    const { publicKey } = await setupRes.json();
    if (!publicKey) {
      return { ok: false, error: 'Push not configured on server. VAPID key missing.' };
    }

    // Subscribe to Web Push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    // Send subscription + pubkey to server
    const subRes = await fetch('/api/push-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pubkey,
        subscription: subscription.toJSON(),
        action: 'subscribe',
      }),
    });

    if (!subRes.ok) {
      // Surface the server's real reason (e.g. Blobs storage detail) so
      // failures are diagnosable instead of a generic message.
      let detail = '';
      try {
        const body = await subRes.json();
        detail = body?.detail || body?.error || '';
      } catch { /* ignore */ }
      return {
        ok: false,
        error: detail
          ? `Failed to register push subscription: ${detail}`
          : 'Failed to register push subscription on server.',
      };
    }

    localStorage.setItem(STORAGE_KEY, 'webpush');
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Disable push notifications.
 */
export async function disablePushNotifications(pubkey: string): Promise<{ ok: boolean; error?: string }> {
  try {
    // Unsubscribe from Web Push
    const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    }

    // Tell server
    try {
      await fetch('/api/push-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pubkey, action: 'unsubscribe' }),
      });
    } catch {}

    localStorage.removeItem(STORAGE_KEY);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Initialize push on app startup for returning users.
 * Cleans up stale state and registers the service worker.
 */
export function initPush(): void {
  // Clean up stale ntfy.sh values from old implementation
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    if (val === 'true' || val === 'false') {
      localStorage.removeItem(STORAGE_KEY);
    }
    localStorage.removeItem('bchnostr:push-topic');
  } catch {}

  if (!isPushSupported()) return;
  if (!isPushEnabled()) return;
  if (Notification.permission !== 'granted') return;

  // Just ensure the SW is registered — it handles push events on its own
  navigator.serviceWorker.register('/sw-push.js').catch(() => {});
}

